import {
  BASIC_INFORMATION,
  CUSTOM_FIELDS,
  MEMBER_FIELDS
} from '@features/forms/components/wizard-steps/fields-step/constants';
import { getFieldsByCategory } from '@features/forms/components/wizard-steps/fields-step/helpers';
import { START_COLLECTION_OPTIONS } from '@features/forms/components/wizard-steps/setup-step/constants';
import {
  AddToMode,
  FormFieldCategory,
  FormFieldData,
  FormSetupData
} from '@features/forms/types';
import { formatISODate } from '@utils/date';
import { roundTo2 } from '@utils/generic';

export type PreviewViewMode = 'mobile' | 'web';

export const slugifyFormName = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export type GstBreakdown = {
  cgstAmount: number;
  sgstAmount: number;
  totalGstAmount: number;
};

/** Exclusive GST is added on top of the fee;
 * only meaningful for the branch's exclusive-GST setup. */
export const getGstBreakdown = (
  setup: Partial<FormSetupData>
): GstBreakdown | undefined => {
  if (!setup.isGstExclusive || !setup.feeAmount) return undefined;

  const feeAmount = Number(setup.feeAmount);
  const cgstAmount = roundTo2((feeAmount * (setup.gstCgstPercent ?? 0)) / 100);
  const sgstAmount = roundTo2((feeAmount * (setup.gstSgstPercent ?? 0)) / 100);

  return {
    cgstAmount,
    sgstAmount,
    totalGstAmount: roundTo2(cgstAmount + sgstAmount)
  };
};

export const computePayerTotal = (setup: Partial<FormSetupData>) => {
  // setup.feeAmount/discountValue can arrive as numeric strings from the edit-flow API.
  const fee = Number(setup.feeAmount ?? 0);
  const feeWithGst = fee + (getGstBreakdown(setup)?.totalGstAmount ?? 0);

  if (!setup.applyDiscount || setup.discountValue === undefined) {
    return feeWithGst;
  }

  const discountValue = Number(setup.discountValue);

  if (setup.discountType === 'percentage') {
    return Math.max(0, feeWithGst - (fee * discountValue) / 100);
  }

  return Math.max(0, feeWithGst - discountValue);
};

export const countFieldsByCategory = (
  fields: FormFieldData[] | undefined,
  category: FormFieldCategory
) => getFieldsByCategory(fields, category).length;

export const getAddToSummaryLabel = (setup: Partial<FormSetupData>) => {
  if (setup.addToMode === 'member_list') return 'Member list';
  return setup.groupName ?? 'A specific group';
};

export const formatPreviewDate = (isoDate?: string) => {
  if (!isoDate) return '';
  return formatISODate({
    date: isoDate,
    format: 'DD MMM YYYY'
  });
};

export const formatPostedDate = () =>
  formatISODate({
    date: new Date(),
    format: 'DD MMMM YYYY'
  });

export const getOrgBadgeLabel = (orgName: string): string => {
  const trimmed = orgName.trim();
  if (!trimmed) return '?';

  const firstChar = trimmed[0].toUpperCase();
  const digit = trimmed.match(/\d/)?.[0];
  return digit ? `${firstChar}${digit}` : firstChar;
};

export const getCollectionStartSummary = (setup: Partial<FormSetupData>) => {
  if (setup.addToMode !== 'group' || !setup.startCollectionFrom) return null;

  const option = START_COLLECTION_OPTIONS.find(
    (item) => item.key === setup.startCollectionFrom
  );

  return option?.value ?? START_COLLECTION_OPTIONS[1].value;
};

export const getPreviewSectionTitle = (category: FormFieldCategory) => {
  if (category === 'basic') return BASIC_INFORMATION.title.toUpperCase();
  if (category === 'member') return MEMBER_FIELDS.title.toUpperCase();
  return CUSTOM_FIELDS.title.toUpperCase();
};

export const sortPreviewFields = (fields: FormFieldData[]) =>
  [...fields].sort((left, right) => left.order - right.order);

const PREVIEW_FIELD_CATEGORY_ORDER: FormFieldCategory[] = [
  'basic',
  'member',
  'custom'
];

export interface PreviewFieldSection {
  category: FormFieldCategory;
  fields: FormFieldData[];
}

/** Basic → member → custom, matching FieldsStep section order. */
export const getPreviewFieldSections = (
  fields: FormFieldData[],
  sortMemberFields: (memberFields: FormFieldData[]) => FormFieldData[]
): PreviewFieldSection[] =>
  PREVIEW_FIELD_CATEGORY_ORDER.map((category) => ({
    category,
    fields: sortPreviewFields(getFieldsByCategory(fields, category))
  }))
    .map((section) =>
      section.category === 'member'
        ? {
            ...section,
            fields: sortMemberFields(section.fields)
          }
        : section
    )
    .filter((section) => section.fields.length > 0);

export interface PublishSummaryItem {
  label: string;
  value: string;
  isAmount?: boolean;
  amount?: number;
  originalAmount?: number;
}

export const hasRegistrationDiscount = (setup: Partial<FormSetupData>) => {
  const feeAmount = Number(setup.feeAmount ?? 0);
  const payerTotal = computePayerTotal(setup);

  return (
    setup.applyDiscount &&
    setup.discountValue !== undefined &&
    feeAmount > payerTotal
  );
};

export interface RegistrationFeeDisplay {
  payerTotal: number;
  originalAmount?: number;
  isFree: boolean;
}

/** Payable total (fee + GST − discount), with the pre-discount GST-inclusive
 * amount struck through when a discount applies. */
export const getRegistrationFeeDisplay = (
  setup: Partial<FormSetupData>
): RegistrationFeeDisplay => {
  const payerTotal = computePayerTotal(setup);
  const feeAmount = Number(setup.feeAmount ?? 0);
  const gstAmount = getGstBreakdown(setup)?.totalGstAmount ?? 0;

  return {
    payerTotal,
    originalAmount: hasRegistrationDiscount(setup)
      ? feeAmount + gstAmount
      : undefined,
    isFree: payerTotal === 0
  };
};

export const buildPublishSummaryItems = (
  setup: Partial<FormSetupData>,
  fields: FormFieldData[] | undefined
): PublishSummaryItem[] => {
  const payerTotal = computePayerTotal(setup);
  const feeAmount = Number(setup.feeAmount ?? 0);
  const gstAmount = getGstBreakdown(setup)?.totalGstAmount ?? 0;
  const collectionStart = getCollectionStartSummary(setup);

  const items: PublishSummaryItem[] = [
    { label: 'Form name', value: setup.name ?? '—' },
    {
      label: 'Added to',
      value: getAddToSummaryLabel(setup)
    }
  ];

  if (collectionStart) {
    items.push({ label: 'Start collection from', value: collectionStart });
  }

  items.push(
    {
      label: 'Number of custom fields',
      value: String(countFieldsByCategory(fields, 'custom'))
    },
    {
      label: 'Number of member fields',
      value: String(countFieldsByCategory(fields, 'member'))
    },
    {
      label: 'Registration fee amount',
      value: '',
      isAmount: true,
      amount: payerTotal,
      ...(hasRegistrationDiscount(setup)
        ? { originalAmount: feeAmount + gstAmount }
        : {})
    }
  );

  if (gstAmount > 0) {
    items.push({
      label: 'GST amount',
      value: '',
      isAmount: true,
      amount: gstAmount
    });
  }

  if (hasRegistrationDiscount(setup)) {
    items.push({
      label: 'Discount applied',
      amount: setup.discountValue,
      isAmount: true,
      value: ''
    });
  }

  if (setup.maxRegistrations !== undefined) {
    items.push({
      label: 'Maximum registrations',
      value: String(setup.maxRegistrations)
    });
  }

  if (setup.autoCloseDate) {
    items.push({
      label: 'Registration close date',
      value: formatPreviewDate(setup.autoCloseDate)
    });
  }

  return items;
};

export const getAddToModeLabel = (mode: AddToMode) =>
  mode === 'group' ? 'A specific group' : 'Member list';
